/**
 * Composable для плавной прокрутки к элементу
 * Заменяет функциональность vue-scrollto для Nuxt 3
 */
export const useScrollTo = () => {
  /**
   * Плавная прокрутка к элементу
   * @param {string|Element} target - CSS селектор или DOM элемент
   * @param {number} duration - Длительность анимации в мс (по умолчанию 500)
   * @param {Object} options - Дополнительные опции
   * @param {number} options.offset - Смещение от верха элемента
   * @param {string} options.easing - Функция easing (linear, easeInOut, easeIn, easeOut)
   * @param {boolean} options.force - Принудительная прокрутка даже если элемент не найден
   */
  const scrollTo = (target, duration = 500, options = {}) => {
    const {
      offset = 0,
      easing = 'easeInOut',
      force = false,
    } = options

    // Получаем элемент
    let element
    if (typeof target === 'string') {
      element = document.querySelector(target)
    } else if (target instanceof Element) {
      element = target
    }

    if (!element && !force) {
      console.warn(`Элемент не найден: ${target}`)
      return Promise.resolve()
    }

    // Если элемент не найден, но force = true, прокручиваем к началу страницы
    if (!element) {
      return smoothScrollTo(0, duration, easing)
    }

    // Вычисляем позицию элемента с учетом offset
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
    const targetPosition = elementPosition - offset

    return smoothScrollTo(targetPosition, duration, easing)
  }

  /**
   * Плавная прокрутка к определенной позиции
   * @param {number} targetPosition - Целевая позиция прокрутки
   * @param {number} duration - Длительность анимации в мс
   * @param {string} easing - Функция easing
   */
  const smoothScrollTo = (targetPosition, duration, easing) => {
    return new Promise((resolve) => {
      const startPosition = window.pageYOffset
      const distance = targetPosition - startPosition
      let startTime = null

      // Функции easing
      const easingFunctions = {
        linear: t => t,
        easeInOut: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
        easeIn: t => t * t,
        easeOut: t => t * (2 - t),
      }

      const easingFunction = easingFunctions[easing] || easingFunctions.easeInOut

      const animation = (currentTime) => {
        if (startTime === null) startTime = currentTime
        const timeElapsed = currentTime - startTime
        const progress = Math.min(timeElapsed / (duration || 100), 1)

        const easedProgress = easingFunction(progress)
        const currentPosition = startPosition + (distance * easedProgress)

        window.scrollTo(0, currentPosition)
        if (progress < 1) {
          requestAnimationFrame(animation)
        } else {
          resolve()
        }
      }

      requestAnimationFrame(animation)
    })
  }

  return {
    scrollTo,
  }
}
